import React, { useEffect } from 'react';
import { useSigma } from '@react-sigma/core';
import { EdgeAttributes, NodeAttributes } from './graphTypes';

// Keeps Sigma's canvas buffers in step with the device pixel ratio.
//
// Sigma 3 re-reads `window.devicePixelRatio` on every render and sets each
// WebGL viewport from it, but it only re-allocates the canvas buffers when the
// container's CSS size changes. So when the pixel ratio changes while the CSS
// size stays put, the viewport no longer matches the buffer: at 2 → 1 the
// graph is drawn into the bottom-left quarter of a 2× buffer (the "graph
// shrank into the corner" bug), at 1 → 2 it is drawn oversized and clipped.
// That is exactly what happens when Chrome captures the tab for screen sharing
// (Meet shares at a lower resolution and lowers the tab's DPR), when a window
// ends up on a display with a different scale after the machine or monitor
// sleeps, or under browser zoom.
//
// Two hooks cover it: a `beforeRender` check that forces a full resize when the
// buffer disagrees with the current DPR (sigma's own resize() runs after this
// event, so the frame draws into correct buffers), and a matchMedia listener so
// a DPR change on an idle canvas triggers a redraw at once instead of waiting
// for the next interaction.
export const PixelRatioSync: React.FC = () => {
  const sigma = useSigma<NodeAttributes, EdgeAttributes>();

  useEffect(() => {
    const syncIfStale = (): boolean => {
      const canvas = sigma.getCanvases().nodes;
      const container = sigma.getContainer();
      if (!canvas || !container) return false;
      // a collapsed container (mid-layout — e.g. DevTools' device toolbar
      // opening, a hidden overlay) has nothing to sync to, and sigma's resize()
      // throws on a zero-size container; it will measure again next frame
      if (container.offsetWidth === 0 || container.offsetHeight === 0)
        return false;
      const expectedWidth = Math.floor(
        container.offsetWidth * window.devicePixelRatio,
      );
      const expectedHeight = Math.floor(
        container.offsetHeight * window.devicePixelRatio,
      );
      // sigma writes `${css * dpr}px` into the attribute, which the canvas
      // truncates — allow a pixel of slack rather than chasing rounding
      const stale =
        Math.abs(canvas.width - expectedWidth) > 1 ||
        Math.abs(canvas.height - expectedHeight) > 1;
      if (stale) sigma.resize(true);
      return stale;
    };

    sigma.on('beforeRender', syncIfStale);

    // matchMedia is the only event browsers fire for a bare DPR change; the
    // query matches the CURRENT ratio, so each change needs a fresh listener
    let media: MediaQueryList | null = null;
    const onDprChange = () => {
      if (syncIfStale()) sigma.scheduleRefresh();
      arm();
    };
    const arm = () => {
      media?.removeEventListener('change', onDprChange);
      media =
        typeof window.matchMedia === 'function'
          ? window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`)
          : null;
      media?.addEventListener('change', onDprChange);
    };
    arm();

    return () => {
      sigma.off('beforeRender', syncIfStale);
      media?.removeEventListener('change', onDprChange);
    };
  }, [sigma]);

  return null;
};
